/**
 * Metro 2 Step 2: Evidence & Supporting Details Component
 * 
 * Structured context collection with:
 * - Selected items summary
 * - System-detected issues (read-only)
 * - Smart conditional questions based on account types
 * - Evidence upload/selection
 * - Optional notes (collapsed by default)
 * - Client safety warnings
 * 
 * NO large freeform textarea!
 */

import React, { useState, useMemo } from 'react';
import {
  FileText,
  Upload,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Info,
  Shield,
  HelpCircle,
  Clock,
  DollarSign,
  GraduationCap,
  Building2,
  Calendar,
  MessageSquare,
  FolderOpen,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Input } from '../ui/input';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

// ============================================================================
// QUESTION CONFIGURATIONS
// ============================================================================

const COLLECTION_QUESTIONS = [
  {
    id: 'recognize_collection',
    question: 'Do you recognize this collection account?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'know_original_creditor',
    question: 'Do you know the original creditor?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: {
      condition: 'Yes',
      field: 'original_creditor_name',
      label: 'Original creditor name',
      placeholder: 'Enter the original creditor name',
    },
  },
  {
    id: 'received_collection_notice',
    question: 'Have you received a collection notice?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'debt_sold_transferred',
    question: 'Has this debt been sold or transferred?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'already_paid_settled',
    question: 'Have you already paid or settled this debt?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: {
      condition: 'Yes',
      field: 'payment_details',
      label: 'Payment/settlement details (optional)',
      placeholder: 'Approx. date and method of payment',
    },
  },
  {
    id: 'have_collector_letters',
    question: 'Do you have any letters from the collector?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
];

const CHARGEOFF_QUESTIONS = [
  {
    id: 'chargeoff_status',
    question: 'Was this account paid, settled, sold, transferred, or discharged?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: {
      condition: 'Yes',
      field: 'chargeoff_status_details',
      label: 'Status details',
      placeholder: 'Paid in full, settled for less, discharged in bankruptcy, etc.',
    },
  },
  {
    id: 'have_settlement_letter',
    question: 'Do you have a settlement letter or paid-in-full letter?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'recognize_balance',
    question: 'Do you recognize the balance being reported?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'have_different_balance_proof',
    question: 'Do you have statements showing a different balance?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
];

const STUDENT_LOAN_QUESTIONS = [
  {
    id: 'protected_status',
    question: 'Were you in deferment, forbearance, in-school status, or administrative forbearance?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: {
      condition: 'Yes',
      field: 'protected_status_type',
      label: 'Type of protected status',
      placeholder: 'Deferment, forbearance, in-school, COVID forbearance, etc.',
    },
  },
  {
    id: 'zero_payment_idr',
    question: 'Did you have a $0 payment due under IDR?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'loan_transferred',
    question: 'Was the loan transferred, consolidated, or rehabilitated?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'have_servicer_proof',
    question: 'Do you have proof from the servicer or StudentAid.gov?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
];

const DISPUTE_NOTATION_QUESTIONS = [
  {
    id: 'disputed_before',
    question: 'Have you disputed this account before?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: null,
  },
  {
    id: 'dispute_target',
    question: 'Did you dispute it with the bureau, furnisher, or both?',
    options: ['Bureau only', 'Furnisher only', 'Both', 'Not sure'],
    followUp: null,
  },
  {
    id: 'have_dispute_proof',
    question: 'Do you have a copy of the dispute letter or certified mail receipt?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
];

const DOFD_QUESTIONS = [
  {
    id: 'know_dofd',
    question: 'Do you know when the account first became delinquent?',
    options: ['Yes', 'No', 'Not sure'],
    followUp: {
      condition: 'Yes',
      field: 'known_dofd',
      label: 'Approximate date of first delinquency',
      placeholder: 'MM/YYYY',
    },
  },
  {
    id: 'have_dofd_proof',
    question: 'Do you have old statements or collector notices showing the original delinquency date?',
    options: ['Yes, I have proof', 'No', 'Not sure'],
    followUp: null,
  },
];

// Evidence types by issue category
const EVIDENCE_SUGGESTIONS = {
  collection: [
    'Collection notice',
    'Debt validation letter',
    'Original creditor statement',
    'Chain of title proof',
    'Settlement letter',
    'Paid-in-full letter',
  ],
  chargeoff: [
    'Account statements',
    'Settlement letter',
    'Paid-in-full letter',
    'Sold/transferred notice',
    'Bankruptcy discharge proof',
  ],
  student_loan: [
    'Deferment proof',
    'Forbearance proof',
    'In-school status proof',
    'IDR / $0 payment proof',
    'StudentAid.gov record',
    'Servicer payment history',
  ],
  dispute_notation: [
    'Bureau dispute confirmation',
    'Direct dispute letter',
    'Certified mail receipt',
    'Furnisher response',
  ],
  dofd: [
    'Old statements',
    'Collector notice',
    'Original creditor records',
    'Previous credit report',
  ],
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Selected Items Summary Card
const SelectedItemsSummary = ({ items, detectedIssues }) => {
  const readyCount = items.filter(i => !i.requires_evidence).length;
  const needsContextCount = items.filter(i => i.requires_evidence || i.detected_issues?.length > 2).length;

  // Get unique issue categories
  const issueCategories = [...new Set(items.flatMap(i => i.detected_issues || i.suggested_issue_categories || []))];

  return (
    <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700" data-testid="selected-items-summary">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          {items.length} Items Selected for Metro 2 Review
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-emerald-700 dark:text-emerald-300">
          {readyCount > 0 && `${readyCount} ${readyCount === 1 ? 'is' : 'are'} ready for Metro 2 review. `}
          {needsContextCount > 0 && `${needsContextCount} may be strengthened with supporting details or documents.`}
          {readyCount === items.length && needsContextCount === 0 && 'All items are ready for Metro 2 review.'}
        </p>

        {/* Issue Categories Chips */}
        {issueCategories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {issueCategories.slice(0, 6).map((issue, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className="text-xs bg-white/60 dark:bg-stone-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600"
              >
                {typeof issue === 'string' ? issue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : issue}
              </Badge>
            ))}
            {issueCategories.length > 6 && (
              <Badge variant="outline" className="text-xs text-stone-500">
                +{issueCategories.length - 6} more
              </Badge>
            )}
          </div>
        )}

        {/* Account Names */}
        <div className="text-xs text-emerald-600 dark:text-emerald-400">
          {items.slice(0, 5).map(i => i.creditor_name).join(', ')}
          {items.length > 5 && `, +${items.length - 5} more`}
        </div>
      </CardContent>
    </Card>
  );
};

// Question Group Component
const QuestionGroup = ({ title, icon: IconComponent, questions, answers, onAnswerChange, iconColor = 'text-emerald-600 dark:text-emerald-400' }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <Card className="border-stone-200 dark:border-stone-700">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-stone-800 dark:text-stone-200 flex items-center gap-2">
                <IconComponent className={`w-4 h-4 ${iconColor}`} />
                {title}
              </CardTitle>
              {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {questions.map((q) => (
              <div key={q.id} className="space-y-2">
                <Label className="text-sm text-stone-700 dark:text-stone-300">{q.question}</Label>
                <RadioGroup
                  value={answers[q.id] || ''}
                  onValueChange={(value) => onAnswerChange(q.id, value)}
                  className="flex flex-wrap gap-3"
                >
                  {q.options.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem 
                        value={option} 
                        id={`${q.id}-${option}`}
                        className="border-emerald-400 text-emerald-600"
                      />
                      <Label 
                        htmlFor={`${q.id}-${option}`}
                        className="text-sm font-normal text-stone-600 dark:text-stone-400 cursor-pointer"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {/* Follow-up field */}
                {q.followUp && answers[q.id] === q.followUp.condition && (
                  <div className="ml-6 mt-2">
                    <Label className="text-xs text-stone-500 dark:text-stone-400">{q.followUp.label}</Label>
                    <Input
                      value={answers[q.followUp.field] || ''}
                      onChange={(e) => onAnswerChange(q.followUp.field, e.target.value)}
                      placeholder={q.followUp.placeholder}
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

// Evidence Section Component
const EvidenceSection = ({ suggestedTypes, selectedDocuments, onDocumentSelect, onUploadClick }) => {
  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="evidence-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-stone-800 dark:text-stone-200 flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          Evidence & Documents
        </CardTitle>
        <CardDescription className="text-stone-500 dark:text-stone-400 text-xs">
          Attach supporting documents if you have them. Evidence can strengthen the dispute but is not always required.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Suggested Evidence Types */}
        {suggestedTypes.length > 0 && (
          <div>
            <Label className="text-xs text-stone-500 dark:text-stone-400 mb-2 block">Suggested for your selected items:</Label>
            <div className="flex flex-wrap gap-2">
              {suggestedTypes.map((type, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-600"
                >
                  {type}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Upload/Select Buttons */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onUploadClick}
            className="text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-600"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-stone-600 dark:text-stone-400"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Select from My Documents
          </Button>
        </div>

        {/* Coming Soon Notice */}
        <Alert className="bg-stone-50 dark:bg-stone-800/50 border-stone-200 dark:border-stone-700">
          <Info className="h-4 w-4 text-stone-500" />
          <AlertDescription className="text-xs text-stone-500 dark:text-stone-400">
            Document upload and selection from your document library is coming soon. 
            You can continue without documents for verification-focused disputes.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

// Optional Notes Section
const OptionalNotesSection = ({ note, onNoteChange }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="link" className="text-stone-600 dark:text-stone-400 p-0 h-auto">
          <Plus className="w-4 h-4 mr-1" />
          Add optional factual note
          {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <Card className="border-amber-200 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10">
          <CardContent className="pt-4 space-y-3">
            {/* Warning */}
            <Alert className="bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-xs text-amber-700 dark:text-amber-300">
                Optional notes should only include facts you can support. Avoid emotional statements, 
                promises to pay, admitting ownership of a debt, guessing dates, or making legal conclusions.
              </AlertDescription>
            </Alert>

            <Textarea
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Example: I disputed this account with TransUnion on March 5 and have a certified mail receipt."
              className="min-h-[80px] text-sm bg-white dark:bg-stone-900 border-amber-200 dark:border-amber-700"
              data-testid="optional-note-input"
            />
            <p className="text-xs text-amber-600 dark:text-amber-400">
              This note will be stored but not directly pasted into the letter without review.
            </p>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Client Safety Warning Card
const ClientSafetyWarning = () => (
  <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700" data-testid="client-safety-warning">
    <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
    <AlertTitle className="text-blue-700 dark:text-blue-300 text-sm">Keep This Factual</AlertTitle>
    <AlertDescription className="text-blue-600 dark:text-blue-400 text-xs">
      Do not admit ownership of a debt, promise payment, guess dates, or add emotional statements. 
      Only provide details you can support with evidence.
    </AlertDescription>
  </Alert>
);

// No Context Needed Message
const NoContextNeeded = () => (
  <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700">
    <CardContent className="py-6 text-center">
      <CheckCircle className="w-10 h-10 text-emerald-500 dark:text-emerald-400 mx-auto mb-3" />
      <h3 className="font-medium text-emerald-800 dark:text-emerald-300 mb-1">No Additional Context Required</h3>
      <p className="text-sm text-emerald-600 dark:text-emerald-400">
        The selected items are ready for Metro 2 technical review. You can continue to the next step.
      </p>
    </CardContent>
  </Card>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Metro2Step2Evidence = ({
  selectedItems = [],
  contextAnswers = {},
  onContextAnswerChange,
  optionalNote = '',
  onOptionalNoteChange,
  selectedDocuments = [],
  onDocumentSelect,
  onUploadClick,
}) => {
  // Determine which question groups to show based on selected items
  const accountTypes = useMemo(() => {
    const types = {
      hasCollections: false,
      hasChargeOffs: false,
      hasStudentLoans: false,
      hasDisputeNotation: false,
      hasDofdIssues: false,
    };

    selectedItems.forEach(item => {
      const statusLower = (item.account_status || '').toLowerCase();
      const typeLower = (item.account_type || item.raw_data?.account_type_detail || '').toLowerCase();
      const detectedIssues = item.detected_issues || item.suggested_issue_categories || [];

      if (item.is_collection || typeLower.includes('collection') || statusLower.includes('collection')) {
        types.hasCollections = true;
      }
      if (item.is_charge_off || statusLower.includes('charge') || statusLower.includes('chargeoff')) {
        types.hasChargeOffs = true;
      }
      if (item.is_student_loan || typeLower.includes('student')) {
        types.hasStudentLoans = true;
      }
      if (detectedIssues.some(i => i.toLowerCase().includes('dispute notation'))) {
        types.hasDisputeNotation = true;
      }
      if (detectedIssues.some(i => i.toLowerCase().includes('dofd') || i.toLowerCase().includes('re-aging') || i.toLowerCase().includes('date'))) {
        types.hasDofdIssues = true;
      }
    });

    return types;
  }, [selectedItems]);

  // Determine if any context is needed
  const needsContext = useMemo(() => {
    return accountTypes.hasCollections || 
           accountTypes.hasChargeOffs || 
           accountTypes.hasStudentLoans ||
           accountTypes.hasDisputeNotation ||
           accountTypes.hasDofdIssues;
  }, [accountTypes]);

  // Get suggested evidence types
  const suggestedEvidenceTypes = useMemo(() => {
    const types = [];
    if (accountTypes.hasCollections) types.push(...EVIDENCE_SUGGESTIONS.collection);
    if (accountTypes.hasChargeOffs) types.push(...EVIDENCE_SUGGESTIONS.chargeoff);
    if (accountTypes.hasStudentLoans) types.push(...EVIDENCE_SUGGESTIONS.student_loan);
    if (accountTypes.hasDisputeNotation) types.push(...EVIDENCE_SUGGESTIONS.dispute_notation);
    if (accountTypes.hasDofdIssues) types.push(...EVIDENCE_SUGGESTIONS.dofd);
    return [...new Set(types)]; // Dedupe
  }, [accountTypes]);

  // Handle answer change - organize by category for proper backend processing
  const handleAnswerChange = (questionId, value, category = null) => {
    // If category provided (from question group), organize nested
    if (category) {
      onContextAnswerChange({
        ...contextAnswers,
        [category]: {
          ...(contextAnswers[category] || {}),
          [questionId]: value,
        },
      });
    } else {
      // Flat storage for misc fields
      onContextAnswerChange({
        ...contextAnswers,
        [questionId]: value,
      });
    }
  };
  
  // Category-specific handlers
  const handleCollectionAnswer = (questionId, value) => handleAnswerChange(questionId, value, 'collections');
  const handleChargeOffAnswer = (questionId, value) => handleAnswerChange(questionId, value, 'chargeoffs');
  const handleStudentLoanAnswer = (questionId, value) => handleAnswerChange(questionId, value, 'student_loans');
  const handleDisputeNotationAnswer = (questionId, value) => handleAnswerChange(questionId, value, 'dispute_notation');
  const handleDofdAnswer = (questionId, value) => handleAnswerChange(questionId, value, 'dofd_reaging');

  return (
    <div className="space-y-6" data-testid="metro2-step2-evidence">
      {/* Main Guidance */}
      <Alert className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700">
        <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <AlertDescription className="text-emerald-700 dark:text-emerald-300 text-sm">
          Review the selected items and add supporting evidence only if you have it. 
          The system will build the Metro 2 technical dispute from the detected reporting patterns.
        </AlertDescription>
      </Alert>

      {/* Selected Items Summary */}
      <SelectedItemsSummary items={selectedItems} />

      {/* Client Safety Warning */}
      <ClientSafetyWarning />

      {/* No Context Needed */}
      {!needsContext && <NoContextNeeded />}

      {/* Conditional Question Groups */}
      {accountTypes.hasCollections && (
        <QuestionGroup
          title="Collection Account Questions"
          icon={Building2}
          iconColor="text-orange-600 dark:text-orange-400"
          questions={COLLECTION_QUESTIONS}
          answers={contextAnswers.collections || {}}
          onAnswerChange={handleCollectionAnswer}
        />
      )}

      {accountTypes.hasChargeOffs && (
        <QuestionGroup
          title="Charge-Off Account Questions"
          icon={DollarSign}
          iconColor="text-red-600 dark:text-red-400"
          questions={CHARGEOFF_QUESTIONS}
          answers={contextAnswers.chargeoffs || {}}
          onAnswerChange={handleChargeOffAnswer}
        />
      )}

      {accountTypes.hasStudentLoans && (
        <QuestionGroup
          title="Student Loan Questions"
          icon={GraduationCap}
          iconColor="text-blue-600 dark:text-blue-400"
          questions={STUDENT_LOAN_QUESTIONS}
          answers={contextAnswers.student_loans || {}}
          onAnswerChange={handleStudentLoanAnswer}
        />
      )}

      {accountTypes.hasDisputeNotation && (
        <QuestionGroup
          title="Prior Dispute Questions"
          icon={MessageSquare}
          iconColor="text-purple-600 dark:text-purple-400"
          questions={DISPUTE_NOTATION_QUESTIONS}
          answers={contextAnswers.dispute_notation || {}}
          onAnswerChange={handleDisputeNotationAnswer}
        />
      )}

      {accountTypes.hasDofdIssues && (
        <QuestionGroup
          title="Date of First Delinquency Questions"
          icon={Calendar}
          iconColor="text-amber-600 dark:text-amber-400"
          questions={DOFD_QUESTIONS}
          answers={contextAnswers.dofd_reaging || {}}
          onAnswerChange={handleDofdAnswer}
        />
      )}

      {/* Evidence Section */}
      <EvidenceSection
        suggestedTypes={suggestedEvidenceTypes}
        selectedDocuments={selectedDocuments}
        onDocumentSelect={onDocumentSelect}
        onUploadClick={onUploadClick}
      />

      {/* Optional Notes */}
      <div className="pt-2">
        <OptionalNotesSection
          note={optionalNote}
          onNoteChange={onOptionalNoteChange}
        />
      </div>

      {/* Evidence Not Required Note */}
      <Alert className="bg-stone-50 dark:bg-stone-800/50 border-stone-200 dark:border-stone-700">
        <Info className="h-4 w-4 text-stone-500" />
        <AlertDescription className="text-xs text-stone-600 dark:text-stone-400">
          Evidence can strengthen the dispute, but Metro 2 review is based primarily on field-level reporting patterns. 
          You may continue without documents unless a selected item requires evidence.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Metro2Step2Evidence;
export {
  SelectedItemsSummary,
  QuestionGroup,
  EvidenceSection,
  OptionalNotesSection,
  ClientSafetyWarning,
  NoContextNeeded,
  COLLECTION_QUESTIONS,
  CHARGEOFF_QUESTIONS,
  STUDENT_LOAN_QUESTIONS,
  DISPUTE_NOTATION_QUESTIONS,
  DOFD_QUESTIONS,
  EVIDENCE_SUGGESTIONS,
};
